import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  FileText, Download, TrendingUp, TrendingDown, Award, RefreshCw,
  Building2, BarChart3, AlertTriangle, CheckCircle, Calendar, Share2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SafetyScorecardExportPage() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [periodDays, setPeriodDays] = useState(30);
  const [companyName, setCompanyName] = useState('');
  const [preparedBy, setPreparedBy] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchPreview();
    fetchHistory();
  }, [periodDays]);

  const fetchPreview = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/safety-scorecard/export/preview?period_days=${periodDays}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreview(response.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load scorecard preview", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/safety-scorecard/export/history?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history');
    }
  };

  const generateScorecard = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_URL}/api/safety-scorecard/export/generate`, {
        period_days: periodDays,
        company_name: companyName || undefined,
        prepared_by: preparedBy || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Download the HTML file
      const blob = new Blob([response.data.content], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({ title: "Success", description: `Scorecard downloaded! Grade: ${response.data.score_summary.grade}` });
      fetchHistory();
    } catch (error) {
      toast({ title: "Error", description: "Failed to generate scorecard", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const getGradeColor = (grade) => {
    const colors = {
      'A': 'bg-green-500',
      'B': 'bg-green-400',
      'C': 'bg-yellow-500',
      'D': 'bg-orange-500',
      'F': 'bg-red-500'
    };
    return colors[grade] || 'bg-gray-500';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingDown className="w-4 h-4 text-green-500" />;
    if (trend === 'worsening') return <TrendingUp className="w-4 h-4 text-red-500" />;
    return <span className="text-gray-400">→</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="safety-scorecard-export-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Award className="w-7 h-7 text-blue-600" />
            Safety Scorecard Export
          </h1>
          <p className="text-gray-600 mt-1">Generate shareable safety performance reports for stakeholders</p>
        </div>
      </div>

      {/* Main Score Card */}
      {preview?.score && (
        <Card className="border-2" style={{ borderColor: preview.score.grade_color }}>
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Grade Circle */}
              <div 
                className="w-36 h-36 rounded-full flex flex-col items-center justify-center border-8"
                style={{ borderColor: preview.score.grade_color }}
              >
                <span className="text-5xl font-bold" style={{ color: preview.score.grade_color }}>
                  {preview.score.grade}
                </span>
                <span className="text-sm text-gray-500">{preview.score.overall_score}/100</span>
              </div>

              {/* Score Details */}
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl font-bold" style={{ color: preview.score.grade_color }}>
                  {preview.score.status}
                </h2>
                <p className="text-gray-600 mt-1">Overall Safety Performance</p>
                <p className="text-sm text-gray-500 mt-2">Based on last {periodDays} days of data</p>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-2xl font-bold">{preview.score.raw_metrics.total_scans}</div>
                  <div className="text-xs text-gray-500">Scans</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-2xl font-bold">{preview.score.raw_metrics.total_incidents}</div>
                  <div className="text-xs text-gray-500">Incidents</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-2xl font-bold">{preview.score.raw_metrics.training_completed}</div>
                  <div className="text-xs text-gray-500">Training</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-2xl font-bold">{preview.score.raw_metrics.audits_completed}</div>
                  <div className="text-xs text-gray-500">Audits</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Export Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Export Settings
            </CardTitle>
            <CardDescription>Configure your scorecard export</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Report Period</Label>
              <Select value={periodDays.toString()} onValueChange={(v) => setPeriodDays(parseInt(v))}>
                <SelectTrigger data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 Days</SelectItem>
                  <SelectItem value="14">Last 14 Days</SelectItem>
                  <SelectItem value="30">Last 30 Days</SelectItem>
                  <SelectItem value="60">Last 60 Days</SelectItem>
                  <SelectItem value="90">Last 90 Days</SelectItem>
                  <SelectItem value="180">Last 6 Months</SelectItem>
                  <SelectItem value="365">Last Year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Company Name (Optional)</Label>
              <Input 
                placeholder="Your Organization" 
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                data-testid="company-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Prepared By (Optional)</Label>
              <Input 
                placeholder="Your Name" 
                value={preparedBy}
                onChange={(e) => setPreparedBy(e.target.value)}
                data-testid="prepared-by-input"
              />
            </div>

            <Button 
              className="w-full" 
              onClick={generateScorecard}
              disabled={generating}
              data-testid="generate-scorecard-btn"
            >
              {generating ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Download Scorecard</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Component Scores */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Score Components
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {preview?.score?.components && Object.entries(preview.score.components).map(([name, data]) => (
              <div key={name}>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium capitalize">{name.replace(/_/g, ' ')}</span>
                  <span className="text-sm text-gray-500">{data.score}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${
                      data.score >= 80 ? 'bg-green-500' : 
                      data.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${data.score}%` }}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Trends
            </CardTitle>
            <CardDescription>{preview?.trends?.period_comparison}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {preview?.trends && (
              <>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium">Incidents</span>
                  <div className="flex items-center gap-2">
                    {getTrendIcon(preview.trends.incidents.trend)}
                    <span className={
                      preview.trends.incidents.trend === 'improving' ? 'text-green-600' :
                      preview.trends.incidents.trend === 'worsening' ? 'text-red-600' : 'text-gray-600'
                    }>
                      {preview.trends.incidents.change_percent > 0 ? '+' : ''}{preview.trends.incidents.change_percent}%
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium">Hazards Detected</span>
                  <div className="flex items-center gap-2">
                    {getTrendIcon(preview.trends.hazards.trend)}
                    <span className={
                      preview.trends.hazards.trend === 'improving' ? 'text-green-600' :
                      preview.trends.hazards.trend === 'worsening' ? 'text-red-600' : 'text-gray-600'
                    }>
                      {preview.trends.hazards.change_percent > 0 ? '+' : ''}{preview.trends.hazards.change_percent}%
                    </span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Departments & Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Department Scores */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Department Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {preview?.departments?.length > 0 ? (
              <div className="space-y-3">
                {preview.departments.slice(0, 5).map((dept, idx) => (
                  <div key={dept.department} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium">
                        {idx + 1}
                      </span>
                      <span className="font-medium">{dept.department}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500">{dept.incidents} incidents</span>
                      <Badge className={
                        dept.score >= 80 ? 'bg-green-100 text-green-700' :
                        dept.score >= 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                      }>
                        {dept.score}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No department data available</p>
            )}
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {preview?.recommendations?.length > 0 ? (
              <ul className="space-y-3">
                {preview.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {idx + 1}
                    </span>
                    <span className="text-sm text-gray-700">{rec}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-center text-gray-500 py-8">No recommendations at this time</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent High-Risk Scans */}
      {preview?.recent_scans?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Recent High-Risk Scans
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Scan #</th>
                    <th className="text-left py-3 px-4 font-medium">Location</th>
                    <th className="text-left py-3 px-4 font-medium">Risk Level</th>
                    <th className="text-center py-3 px-4 font-medium">Hazards</th>
                    <th className="text-left py-3 px-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.recent_scans.map((scan, idx) => (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="py-3 px-4 font-medium">{scan.scan_number}</td>
                      <td className="py-3 px-4">{scan.location}</td>
                      <td className="py-3 px-4">
                        <Badge className={scan.risk_level === 'critical' ? 'bg-red-500' : 'bg-orange-500'}>
                          {scan.risk_level?.toUpperCase()}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-center">{scan.hazards_count}</td>
                      <td className="py-3 px-4 capitalize">{scan.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export History */}
      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Recent Exports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium">{item.company_name || 'Safety Scorecard'}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(item.generated_at).toLocaleDateString()} • {item.period_days} days
                      </p>
                    </div>
                  </div>
                  <Badge className={getGradeColor(item.score_data?.grade)}>
                    Grade: {item.score_data?.grade}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
